import {
  AddClassPostCommentMutation as AddClassPostCommentMutationType,
  ClassPost_ClassPostWithOnlyCommentsFragment
} from "@outschool/gql-frontend-generated";
import { DataProxy, useMutation } from "@outschool/ui-apollo";
import {
  AddClassPostCommentMutation,
  AddClassPostCommentMutationVariables,
  ClassPostWithOnlyCommentsFragment,
  addClassPostCommentMutation
} from "@outschool/ui-components-classroom";
import { addTypeName, validOptimisticResponse } from "@outschool/ui-utils";
import React from "react";

type ClassPostComment = AddClassPostCommentMutationType["addClassPostComment"];
type Attachment = ClassPostComment["messageContent"]["attachments"][0];
type User = Pick<ClassPostComment["sender"], "uid" | "name" | "photo">;
type Learner = Pick<ClassPostComment["sender"], "uid" | "name" | "avatar">;

interface UpdateClassroomCacheOptions {
  classPostUid: string;
  newClassPostComment: Omit<ClassPostComment, "replies">;
  classroomQueryVariables?: object;
}

interface AddClassPostCommentMutationInput {
  currentUser?: User;
  classroomQueryVariables?: object;
  updateClassroomCache: (
    store: DataProxy,
    options: UpdateClassroomCacheOptions
  ) => void;
}

export function useAddClassPostCommentMutation({
  currentUser,
  classroomQueryVariables = {},
  updateClassroomCache
}: AddClassPostCommentMutationInput) {
  const [mutate] = useMutation<
    AddClassPostCommentMutation,
    AddClassPostCommentMutationVariables
  >(addClassPostCommentMutation);

  return React.useCallback(
    (
      classPostUid: string,
      text: string,
      attachments: Attachment[],
      video: Attachment,
      learner?: Learner,
      mentions?: ({ id: string } | string)[],
      backgroundColor?: string
    ) => {
      const attachmentUids = attachments ? attachments.map(a => a.uid) : [];
      const videoUid = video ? video.uid : undefined;
      const mentionsUids = mentions
        ? mentions.map(m => (typeof m === "string" ? m : m.id))
        : [];
      const classPostCommentInput = {
        classPostUid,
        learnerUid: learner?.uid,
        messageContent: {
          text,
          attachmentUids,
          videoUid,
          mentionsUids
        },
        backgroundColor
      };
      return mutate({
        variables: { classPostCommentInput, plainText: false },
        optimisticResponse: validOptimisticResponse({
          __typename: "Mutation",
          addClassPostComment: {
            __typename: "ClassPostComment",
            uid: new Date(),
            sendAt: new Date(),
            sender: {
              __typename: "MessageSender",
              uid: learner?.uid || currentUser?.uid,
              name: learner?.name || currentUser?.name,
              photo: currentUser?.photo || null,
              avatar: learner?.avatar || null,
              isLearner: learner ? true : false
            },
            messageContent: {
              __typename: "MessageContent",
              uid: new Date(),
              text,
              attachments: addTypeName("Attachment", attachments),
              video: addTypeName("Attachment", video),
              mentionsUids,
              editedAt: null
            },
            publishAt: new Date(),
            mentionedEnrollmentUids: mentionsUids,
            mentionedTeacherUid: null,
            backgroundColor: backgroundColor
          }
        }),
        update(store, { data }) {
          if (!data?.addClassPostComment) {
            console.error(
              "AddClassPostCommentMutation update function called with",
              { data }
            );
            return;
          }
          // Attempt to update cached values for both ClassroomQuery and ClassPostWithOnlyCommentsFragment
          // (Classroom needs to update both since "Show X more comments" = ClassPostWithOnlyCommentsFragment)
          try {
            // This can trigger an Invariant Violation if ClassroomQuery was never executed
            // (e.g. user loads the SinglePost page directly and adds a comment to the post)
            updateClassroomCache(store, {
              classPostUid,
              newClassPostComment: data.addClassPostComment,
              classroomQueryVariables
            });
          } catch (e) {
            if (e.name !== "Invariant Violation") {
              throw e;
            }
          }
          try {
            // This can trigger an Invariant Violation if ClassPostWithOnlyCommentsFragment was never executed
            // (e.g.user loads the Classroom page and adds a comment to a post without clicking "Show X more comments")
            updateClassPostWithOnlyCommentsCache(store, {
              classPostUid,
              newClassPostComment: data.addClassPostComment
            });
          } catch (e) {
            if (e.name !== "Invariant Violation") {
              throw e;
            }
          }
        }
      });
    },
    [mutate, currentUser, updateClassroomCache, classroomQueryVariables]
  );
}

const CLASS_POST_TYPE = "ClassPost";
function updateClassPostWithOnlyCommentsCache(
  store: DataProxy,
  { newClassPostComment, classPostUid }: $TSFixMe
) {
  const fragmentInfo = {
    id: `${classPostUid}${CLASS_POST_TYPE}`,
    fragment: ClassPostWithOnlyCommentsFragment,
    fragmentName: "ClassPost_classPostWithOnlyComments",
    variables: {
      plainText: false
    }
  };
  const data =
    store.readFragment<ClassPost_ClassPostWithOnlyCommentsFragment>(
      fragmentInfo
    );
  if (data && data.comments) {
    store.writeFragment({
      ...fragmentInfo,
      data: {
        ...data,
        comments: [...data.comments, newClassPostComment]
      }
    });
  }
}
